import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Scales, Clock, Money, FileText, Warning, UserFocus } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { FAQ } from '@/components/FAQ';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: 'easeOut' },
};

const Trabalhista = () => {
  const situacoes = [
    { icon: Money, title: 'Verbas rescisórias' },
    { icon: Clock, title: 'Horas extras não pagas' },
    { icon: FileText, title: 'Reconhecimento de vínculo de emprego' },
    { icon: Warning, title: 'Rescisão indireta' },
    { icon: UserFocus, title: 'Assédio moral' },
    { icon: Scales, title: 'Reversão ou discussão de justa causa' },
  ];

  const faqItems = [
    {
      question: 'Quando procurar um advogado trabalhista?',
      answer:
        'Quando houver dúvida ou conflito relacionado a verbas rescisórias, horas extras, vínculo de emprego, rescisão indireta, justa causa, assédio moral ou outras irregularidades na relação de trabalho.',
    },
    {
      question: 'O caso pode ser analisado antes de qualquer medida?',
      answer:
        'Sim. A análise inicial é importante para avaliar documentos, identificar direitos envolvidos e orientar corretamente sobre as possibilidades do caso.',
    },
    {
      question: 'Toda questão trabalhista necessariamente resulta em processo?',
      answer:
        'A medida adequada depende da natureza da situação, da documentação e da estratégia jurídica aplicável ao caso concreto.',
    },
    {
      question: 'A RDC Advogados atende no Rio de Janeiro?',
      answer:
        'Sim. O escritório possui atuação no Rio de Janeiro e está localizado no Centro da cidade, na Rua São José, nº 46, Grupo 506/507.',
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-24 bg-[#2A2A2A] text-white" data-testid="trabalhista-hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="max-w-4xl">
            <h1 className="text-4xl sm:text-5xl font-bold playfair tracking-tight mb-6">
              Direito Trabalhista no Rio de Janeiro
            </h1>
            <p className="text-xl text-white/90 leading-relaxed">
              Atuação jurídica em demandas trabalhistas com análise criteriosa, estratégia consistente e orientação
              clara desde o primeiro contato.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Opening Text */}
      <section className="py-24 bg-white" data-testid="trabalhista-intro-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="max-w-4xl mx-auto">
            <p className="text-lg text-[#737373] leading-relaxed mb-6">
              Questões trabalhistas frequentemente envolvem não apenas direitos patrimoniais, mas também
              estabilidade, dignidade e segurança profissional. Situações como verbas rescisórias não pagas
              corretamente, horas extras, vínculo sem registro, rescisão indireta, justa causa e assédio moral
              exigem leitura técnica do caso concreto e avaliação responsável das medidas possíveis.
            </p>
            <p className="text-lg text-[#737373] leading-relaxed">
              A RDC Advogados atua com foco na análise jurídica dessas demandas, oferecendo ao cliente uma condução
              séria, objetiva e estrategicamente estruturada no Rio de Janeiro.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Situações */}
      <section className="py-24 bg-[#FAFAFA]" data-testid="trabalhista-situacoes-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#2A2A2A] playfair mb-4">
              Situações frequentemente analisadas
            </h2>
            <p className="text-lg text-[#737373]">
              Demandas trabalhistas que podem exigir análise jurídica
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {situacoes.map((item, index) => (
              <motion.div
                key={item.title || item.id || index}
                {...fadeUp}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                className="bg-white border border-[#2A2A2A]/10 rounded-sm p-6 flex items-center space-x-4"
                data-testid={`situacao-${index}`}
              >
                <item.icon size={32} weight="duotone" className="text-[#A98C67] flex-shrink-0" />
                <span className="text-[#2A2A2A] font-medium">{item.title}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How we work */}
      <section className="py-24 bg-white" data-testid="trabalhista-work-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeUp}>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#2A2A2A] playfair mb-6">
                Como o escritório atua
              </h2>
              <p className="text-xl text-[#737373] mb-6 leading-relaxed">
                Atuação trabalhista com profundidade técnica e direção estratégica
              </p>
              <p className="text-lg text-[#737373] leading-relaxed">
                Cada situação é analisada a partir dos fatos, da documentação disponível e do contexto da relação
                de trabalho. O escritório orienta o cliente sobre os direitos envolvidos, a viabilidade jurídica da
                demanda e a estratégia adequada para a condução do caso, sempre com comunicação clara e postura
                responsável.
              </p>
            </motion.div>
            <motion.div {...fadeUp} transition={{ delay: 0.2, duration: 0.6 }}>
              <img
                src="https://images.unsplash.com/photo-1571055931484-22dce9d6c510"
                alt="Escritório jurídico"
                className="rounded-sm shadow-lg w-full h-auto"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why seek guidance */}
      <section className="py-24 bg-[#FAFAFA]" data-testid="trabalhista-guidance-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#2A2A2A] playfair mb-6">
              Por que buscar orientação adequada
            </h2>
            <p className="text-xl text-[#737373] mb-8 leading-relaxed">
              A importância de uma análise precisa
            </p>
            <p className="text-lg text-[#737373] leading-relaxed">
              Em matéria trabalhista, detalhes documentais e circunstâncias específicas podem alterar de forma
              relevante a leitura do caso. Uma orientação jurídica tecnicamente consistente permite compreender
              melhor a situação, evitar decisões precipitadas e adotar o caminho mais adequado com maior segurança.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-white" data-testid="trabalhista-faq-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#2A2A2A] playfair mb-4">Perguntas frequentes</h2>
          </motion.div>
          <motion.div {...fadeUp}>
            <FAQ items={faqItems} />
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#2A2A2A] text-white" data-testid="trabalhista-cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl sm:text-4xl font-bold playfair mb-6">
              Seu caso trabalhista exige leitura técnica e orientação segura
            </h2>
            <p className="text-lg text-white/80 mb-8">
              Entre em contato com a RDC Advogados para apresentar sua situação e receber uma análise inicial com
              clareza e seriedade.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contato">
                <Button
                  size="lg"
                  data-testid="trabalhista-contact-button"
                  className="bg-white text-[#2A2A2A] hover:bg-white/90 rounded-sm font-medium px-8 py-6 text-base"
                >
                  Solicitar análise trabalhista
                </Button>
              </Link>
              <a href="https://wa.me/552128880258" target="_blank" rel="noopener noreferrer">
                <Button
                  size="lg"
                  
                  data-testid="trabalhista-whatsapp-button"
                  className="bg-[#25D366] text-white hover:bg-[#20BA5A] border-0 rounded-sm font-medium px-8 py-6 text-base"
                >
                  Falar com o escritório
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Trabalhista;